<script setup>
import { ref, computed } from 'vue'
import { Building2, Smartphone, ArrowLeft, ArrowRight, Check } from 'lucide-vue-next'

const active = ref(0)
const isDragging = ref(false)
const dragOffset = ref(0)

let startX = 0
let moved = false
let suppressClick = false

const trackStyle = computed(() => ({
  transform: `translate3d(calc(${-active.value * 50}% + ${dragOffset.value}px), 0, 0)`
}))

const goTo = (index) => {
  active.value = index
}

const next = () => {
  active.value = active.value === 0 ? 1 : 0
}

const prev = () => {
  active.value = active.value === 0 ? 1 : 0
}

const onPointerDown = (event) => {
  if (event.pointerType === 'mouse' && event.button !== 0) return
  isDragging.value = true
  startX = event.clientX
  moved = false
  dragOffset.value = 0
  event.currentTarget.setPointerCapture?.(event.pointerId)
}

const onPointerMove = (event) => {
  if (!isDragging.value) return
  const delta = event.clientX - startX
  dragOffset.value = Math.max(-120, Math.min(120, delta))
  if (Math.abs(delta) > 10) moved = true
}

const endDrag = (event) => {
  if (!isDragging.value) return
  isDragging.value = false

  if (dragOffset.value < -50) next()
  else if (dragOffset.value > 50) prev()

  dragOffset.value = 0

  if (moved) {
    suppressClick = true
    window.setTimeout(() => {
      suppressClick = false
    }, 80)
  }

  event.currentTarget.releasePointerCapture?.(event.pointerId)
}

const cancelDrag = (event) => {
  isDragging.value = false
  dragOffset.value = 0
  event.currentTarget.releasePointerCapture?.(event.pointerId)
}

const onTrackClick = (event) => {
  if (!suppressClick) return
  event.preventDefault()
  event.stopPropagation()
}
</script>

<template>
  <div class="relative w-full max-w-5xl">
    <h2 class="sr-only">Elige cómo usar LLEVO</h2>
    <p class="sr-only" aria-live="polite">
      {{ active === 0 ? 'Mostrando opciones para negocios' : 'Mostrando opciones para usuarios' }}
    </p>

    <!-- Controles -->
    <div class="mb-3 flex items-center justify-between gap-4">
      <div
        class="inline-flex rounded-full border border-white/15 bg-[#0a1120]/60 p-1 backdrop-blur"
        role="tablist"
        aria-label="Elige cómo usar LLEVO"
      >
        <button
          id="audience-tab-negocios"
          type="button"
          role="tab"
          :aria-selected="active === 0"
          aria-controls="audience-panel-negocios"
          class="whitespace-nowrap rounded-full px-3 py-2 text-[10px] font-black uppercase tracking-[0.08em] transition-all duration-300 sm:px-4 sm:text-xs sm:tracking-[0.12em]"
          :class="active === 0 ? 'bg-white text-[#0a1120] shadow-sm' : 'text-slate-300 hover:text-white'"
          @click="goTo(0)"
        >
          Soy negocio
        </button>
        <button
          id="audience-tab-usuarios"
          type="button"
          role="tab"
          :aria-selected="active === 1"
          aria-controls="audience-panel-usuarios"
          class="whitespace-nowrap rounded-full px-3 py-2 text-[10px] font-black uppercase tracking-[0.08em] transition-all duration-300 sm:px-4 sm:text-xs sm:tracking-[0.12em]"
          :class="active === 1 ? 'bg-white text-[#0a1120] shadow-sm' : 'text-slate-300 hover:text-white'"
          @click="goTo(1)"
        >
          Soy usuario
        </button>
      </div>

      <div class="flex items-center gap-2">
        <button
          type="button"
          aria-label="Ver opción anterior"
          class="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-all duration-200 hover:border-[#f0611e]/50 hover:bg-[#f0611e]/15"
          @click="prev"
        >
          <ArrowLeft :size="16" />
        </button>
        <button
          type="button"
          aria-label="Ver opción siguiente"
          class="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-all duration-200 hover:border-[#f0611e]/50 hover:bg-[#f0611e]/15"
          @click="next"
        >
          <ArrowRight :size="16" />
        </button>
      </div>
    </div>

    <!-- Carrusel -->
    <div
      class="relative overflow-hidden rounded-[2rem] border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.35)] [touch-action:pan-y]"
      :class="isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'"
      role="region"
      aria-roledescription="carrusel"
      aria-label="Opciones para negocios y usuarios"
      tabindex="0"
      @keydown.left.prevent="prev"
      @keydown.right.prevent="next"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="endDrag"
      @pointercancel="cancelDrag"
      @click.capture="onTrackClick"
    >
      <div
        class="flex w-[200%] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
        :class="isDragging ? 'transition-none' : ''"
        :style="trackStyle"
      >
        <!-- Slide: Negocios -->
        <div
          id="audience-panel-negocios"
          class="w-1/2 shrink-0"
          role="tabpanel"
          aria-labelledby="audience-tab-negocios"
          :aria-hidden="active !== 0"
          :inert="active !== 0"
        >
          <div class="relative flex min-h-[340px] h-full flex-col overflow-hidden bg-[#f0611e] p-7 text-[#0a1120] sm:p-9 lg:p-10">
            <Building2
              :size="260"
              :stroke-width="0.9"
              aria-hidden="true"
              class="pointer-events-none absolute -bottom-20 -right-10 text-[#0a1120]/10"
            />
            <div class="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-[#ffb38a]/40 blur-3xl"></div>

            <div class="relative grid items-center gap-8 sm:grid-cols-12">
              <div class="sm:col-span-7">
                <p class="text-[10px] font-black uppercase tracking-[0.2em] text-[#0a1120]">Para negocios</p>
                <h3 class="mt-2 text-3xl font-black tracking-tight sm:text-4xl">Soy negocio</h3>
                <p class="mt-3 max-w-md text-sm font-semibold leading-relaxed text-[#0a1120]">
                  Despacha con una red confiable y controla cada pedido desde tu panel. Tú vendes; nosotros movemos.
                </p>

                <div class="mt-5 flex flex-wrap gap-2">
                  <span
                    v-for="item in ['Panel de negocio', 'Tarifas automáticas', 'Múltiples despachos']"
                    :key="item"
                    class="inline-flex items-center gap-1.5 rounded-full border border-[#0a1120]/15 bg-[#0a1120]/5 px-3 py-1.5 text-[11px] font-bold"
                  >
                    <Check :size="12" />
                    {{ item }}
                  </span>
                </div>

                <router-link
                  to="/postular-negocio"
                  class="group mt-6 inline-flex items-center gap-2 rounded-xl bg-[#0a1120] px-5 py-3 text-sm font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#1c2b42]"
                >
                  Registrar mi negocio
                  <ArrowRight :size="16" class="transition-transform group-hover:translate-x-1" />
                </router-link>
              </div>

              <div class="hidden sm:col-span-5 sm:flex sm:justify-center">
                <div class="relative flex h-44 w-44 items-center justify-center rounded-[2.5rem] bg-[#0a1120] text-[#f0611e] shadow-2xl">
                  <Building2 :size="72" :stroke-width="1.3" />
                  <span class="absolute -inset-3 rounded-[3rem] border border-[#0a1120]/20"></span>
                  <span class="absolute -inset-8 rounded-full border border-[#0a1120]/10"></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Slide: Usuarios -->
        <div
          id="audience-panel-usuarios"
          class="w-1/2 shrink-0"
          role="tabpanel"
          aria-labelledby="audience-tab-usuarios"
          :aria-hidden="active !== 1"
          :inert="active !== 1"
        >
          <div class="relative flex min-h-[340px] h-full flex-col overflow-hidden bg-white p-7 text-[#0a1120] sm:p-9 lg:p-10">
            <Smartphone
              :size="260"
              :stroke-width="0.9"
              aria-hidden="true"
              class="pointer-events-none absolute -bottom-20 -right-10 text-[#0a1120]/5"
            />
            <div class="pointer-events-none absolute -left-16 -top-20 h-64 w-64 rounded-full bg-[#f0611e]/10 blur-3xl"></div>

            <div class="relative grid items-center gap-8 sm:grid-cols-12">
              <div class="sm:col-span-7">
                <p class="text-[10px] font-black uppercase tracking-[0.2em] text-[#c2410c]">Para usuarios</p>
                <h3 class="mt-2 text-3xl font-black tracking-tight sm:text-4xl">Soy usuario</h3>
                <p class="mt-3 max-w-md text-sm font-semibold leading-relaxed text-slate-600">
                  Pide en la app y sigue tu pedido en el mapa, en tiempo real. Rápido, seguro y sin llamadas para coordinar.
                </p>

                <div class="mt-5 flex flex-wrap gap-2">
                  <span
                    v-for="item in ['Rastreo en tiempo real', 'Historial de envíos', 'Soporte humano']"
                    :key="item"
                    class="inline-flex items-center gap-1.5 rounded-full bg-[#fff1ea] px-3 py-1.5 text-[11px] font-bold text-[#2b4162]"
                  >
                    <Check :size="12" class="text-[#c2410c]" />
                    {{ item }}
                  </span>
                </div>

                <a
                  href="#app-usuarios"
                  class="group mt-6 inline-flex items-center gap-2 rounded-xl bg-[#c2410c] px-5 py-3 text-sm font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#9a3412]"
                >
                  Descargar la app
                  <ArrowRight :size="16" class="transition-transform group-hover:translate-x-1" />
                </a>
              </div>

              <div class="hidden sm:col-span-5 sm:flex sm:justify-center">
                <div class="relative flex h-44 w-44 items-center justify-center rounded-[2.5rem] bg-[#f0611e] text-white shadow-2xl">
                  <Smartphone :size="72" :stroke-width="1.3" />
                  <span class="absolute -inset-3 rounded-[3rem] border border-[#f0611e]/20"></span>
                  <span class="absolute -inset-8 rounded-full border border-[#f0611e]/10"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Indicadores -->
    <div class="mt-4 flex items-center">
      <button
        v-for="(_, index) in 2"
        :key="index"
        type="button"
        :aria-label="index === 0 ? 'Ver opción para negocios' : 'Ver opción para usuarios'"
        class="group flex h-8 w-8 items-center justify-center"
        @click="goTo(index)"
      >
        <span
          class="h-2 rounded-full transition-all duration-300"
          :class="active === index ? 'w-8 bg-[#f0611e]' : 'w-2 bg-white/25 group-hover:bg-white/40'"
        ></span>
      </button>
    </div>
  </div>
</template>
